import { Component, Input, OnInit } from '@angular/core';
import { EventType, EventTypeCategory} from '../../../../../../../../../models/event-type';
import { CustomerCredit, CustomerEventVoucher } from '../../../../../../../../../models/customer';
import { AccountService } from '../../../../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { EventTypeService } from '../../../../../../../../../services/event-type/event-type.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventTypeStatus } from '../../../../../../../../../models/event-type-enums';


@Component({
  selector: 'app-add-customer-credit-or-voucher-modal',
  templateUrl: './add-customer-credit-or-voucher-modal.component.html',
  styleUrls: ['./add-customer-credit-or-voucher-modal.component.css']
})
export class AddCustomerCreditOrVoucherModalComponent implements OnInit {

  readonly TYPE_CREDIT = 'Credit';
  readonly TYPE_VOUCHER = 'Voucher';

  @Input() customerId: number;
  @Input() savedCallback: (successMessage: string) => undefined;

  public eventTypeList: EventType[];
  public eventCategoryList: EventTypeCategory[];

  public selectedType = this.TYPE_CREDIT;
  public credit: CustomerCredit = {
    id: null,
    url: '',
    amount_total: null,
    amount_remaining: null,
    is_expired: false,
    is_removed: false,
    will_apply_to_recurring_payments: false,
    only_applies_to_recurring_payments: false,
    date_added_str: '',
    created_at: '',
    updated_at: ''
  };
  public voucher: CustomerEventVoucher = {
    id: null,
    url: '',
    event_type_id: null,
    event_type_url: '',
    event_type_name: '',
    event_category_id: null,
    event_category_url: '',
    event_category_name: '',
    number_of_uses_total: null,
    number_of_uses_remaining: null,
    is_expired: false,
    is_removed: false,
    date_added: '',
    date_added_str: '',
    created_at: '',
    updated_at: ''
  };
  public useVoucherEventType = true;

  public serverError: number = null;

  public loadingSubmit = false;
  public loadingEventTypes = true;
  public loadingEventCategories = true;

  public errors: any = {};
  public errorMessage = '';

  public EventTypeStatus = EventTypeStatus;

  constructor(
    public activeModal: NgbActiveModal,
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private customerService: CustomerService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.loadEventTypes();
    this.loadEventCategories();
  }

  public handleSubmit() {
    if (this.selectedType === this.TYPE_CREDIT) {
      this.handleCreditSubmit();
    } else if (this.selectedType === this.TYPE_VOUCHER) {
      this.handleVoucherSubmit();
    }
  }

  public isLoading() {
    let isLoading = this.loadingSubmit;

    if (this.selectedType === this.TYPE_VOUCHER) {
      isLoading = isLoading || this.loadingEventTypes;
    }

    return isLoading;
  }

  public clearVoucherEventTypeAndCategory() {
    this.voucher.event_type_id = null;
    this.voucher.event_category_id = null;
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {}
      )
      .then(response => {
        this.eventTypeList = response.results.filter(et => et.status !== EventTypeStatus.draft);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventTypes = false;
      });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(
        1,
        3000
      )
      .then(response => {
        this.eventCategoryList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventCategories = false;
      });
  }

  private handleCreditSubmit() {
    this.errorMessage = '';
    this.errors = {};

    if (!this.credit.amount_total) {
      this.errors.amount_total = 'This field is required.';
      this.errorMessage = 'Please correct the errors above and try again.';
    } else if (this.credit.amount_total < 0.01) {
      this.errors.amount_total = 'This value must be at least 0.01.';
      this.errorMessage = 'Please correct the errors above and try again.';
    }
    if (this.errorMessage !== '') {
      return;
    }

    if (!this.credit.will_apply_to_recurring_payments) {
      this.credit.only_applies_to_recurring_payments = false;
    }

    this.loadingSubmit = true;

    this.customerService
      .addCustomerCredit(
        this.customerId,
        this.credit
      )
      .then(createdCredit => {
        this.savedCallback('Credit added successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add credits.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private handleVoucherSubmit() {
    this.errorMessage = '';
    this.errors = {};

    if (this.useVoucherEventType && !this.voucher.event_type_id) {
      this.errors.event_type_id = 'This field is required.';
      this.errorMessage = 'Please correct the errors above and try again.';
    }
    if (!this.useVoucherEventType && !this.voucher.event_category_id) {
      this.errors.event_category_id = 'This field is required.';
      this.errorMessage = 'Please correct the errors above and try again.';
    }
    if (!this.voucher.number_of_uses_total) {
      this.errors.number_of_uses_total = 'This field is required.';
      this.errorMessage = 'Please correct the errors above and try again.';
    } else if (this.voucher.number_of_uses_total < 1) {
      this.errors.number_of_uses_total = 'This value must be at least 1.';
      this.errorMessage = 'Please correct the errors above and try again.';
    }
    if (this.errorMessage !== '') {
      return;
    }

    this.loadingSubmit = true;

    this.customerService
      .addCustomerEventVoucher(
        this.customerId,
        this.voucher
      )
      .then(createdVoucher => {
        this.savedCallback('Voucher added successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add vouchers.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  /**
   * Whether the voucher's selected event category is removed (soft-deleted),
   * i.e. absent from the active-category list. Drives the pinned
   * "<name> (Removed)" option so the select isn't blank when editing a voucher
   * in a removed category (PRO-1310); follows the removed tax rate convention.
   */
  public eventCategoryRemoved(eventCategoryId: number): boolean {
    if ((eventCategoryId === null) || (eventCategoryId === undefined)) {
      return false;
    }
    if (!this.eventCategoryList) { return true; }

    for (const eventCategory of this.eventCategoryList) {
      if (eventCategory.id === eventCategoryId) { return false; }
    }

    return true;
  }
}
